import React, { useMemo } from 'react';
import List from '@material-ui/core/List';
import { DefaultDivider } from 'services/UIUtils';
import CPPListElement, {
  GTPALList,
  HistoryCPPListElement,
} from 'components/Patient/CPP/CPPListElement';
import CPPMedicationListElement from 'components/Patient/CPP/CPPMedicationListElement';
import CPPListDocuments from 'components/Patient/CPP/CPPListDocuments';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import CPPUtils from 'services/CPPUtils';
import CPPListMetrics from 'components/Patient/CPP/CPPListMetrics';
import CPPInformationPanel from 'components/Patient/CPP/CPPInformationPanel';
import PerformanceUtils from 'services/PerformanceUtils';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import CPPRemindersPanel from 'components/Patient/CPP/CPPRemindersPanel';
import { setFormValues } from 'hooks/useReduxForm';
import Icon from 'components/Utils/NewIcon';
import { HISTORY_TYPES } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';

const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];

const CPPHealthRecordsPanel = ({ patient, bubble, setActiveCPPTab, onSearch }) => {
  const dispatch = useAppDispatch();
  const patientId = patient?.id;

  const healthRecords =
    useAppSelector((state) => state.healthRecords.byId[patientId]) || DEFAULT_OBJECT;
  const patientTasks = useAppSelector((state) => state.tasks.byId[patientId]) || DEFAULT_ARRAY;

  const hr = healthRecords.healthRecords || DEFAULT_OBJECT;

  const filteredImmunizations = useMemo(
    () => CPPUtils.filterScheduledAndOverdueImmunizations(healthRecords.immunizations || []),
    [healthRecords.immunizations],
  );

  const hasPendingDrugs = useMemo(() => {
    const drugs = healthRecords.administered_drugs || DEFAULT_ARRAY;
    return !!drugs.find((drug) => CPPUtils.isPendingImmunization(drug));
  }, [healthRecords.administered_drugs]);

  function onReadminister(drug) {
    setActiveCPPTab('administered_drugs');
    const value = CPPUtils.getImmunizationEditElement({
      ...drug,
      value: {
        ...drug.value,
        id: undefined,
        lot: undefined,
        given_date: undefined,
        expiry_date: undefined,
        given_by: undefined,
      },
    });
    const formName = CPPUtils.getFormName('administered_drugs', patientId);
    dispatch(setFormValues({ formName, values: value }));
  }

  return (
    <div className="health-records-panel scroll-list underside-right-panel full-height-panel">
      <ErrorBoundary>
        <List>
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Allergies"
            field="allergies"
            icon="head-side-cough"
            items={hr.allergies}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Private Notes"
            field="notes"
            icon="notes"
            items={hr.notes}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Active Issues"
            field="active_issues"
            icon="clipboard-list"
            items={hr.active_issues}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Medical History"
            field="medical_issues"
            icon="book-medical"
            items={hr.medical_issues}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <HistoryCPPListElement
            patient={patient}
            bubble={bubble}
            title="History"
            field="history"
            icon="clock-rotate-left"
            items={hr.history}
            setActiveCPPTab={setActiveCPPTab}
            type={HISTORY_TYPES.surgical_history}
          />
          <DefaultDivider />
          <CPPMedicationListElement
            patient={patient}
            items={hr.medications}
            setActiveCPPTab={setActiveCPPTab}
            onSearch={onSearch}
            icons={(item) => [
              <Icon
                key={item.id + '-search-icon'}
                src="magnifying-glass"
                alt="Search"
                className="search-icon"
                onClick={() => onSearch(item)}
              />,
            ]}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Consultants"
            field="consultants"
            icon="users-medical"
            items={hr.consultants}
            setActiveCPPTab={setActiveCPPTab}
            icons={(item) => [
              <Icon
                key={item.id + '-search-icon'}
                src="magnifying-glass"
                alt="Search"
                className="search-icon"
                onClick={() => onSearch(`"${item?.value?.name}"`)}
              />,
            ]}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Immunizations"
            field="immunizations"
            icon="vial-virus"
            items={filteredImmunizations}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Med Admin..."
            field="administered_drugs"
            icon="syringe"
            items={healthRecords.administered_drugs}
            setActiveCPPTab={setActiveCPPTab}
            collapse={true}
            collapseInitialValue={!hasPendingDrugs}
            badge={(healthRecords.administered_drugs || []).length}
            icons={(drug, props) => [
              <Icon
                key="repeat"
                src="repeat"
                alt="Readminister"
                showTooltip={true}
                width="15px"
                onClick={() => onReadminister(drug)}
              />,
            ]}
          />
          <DefaultDivider />
          <HistoryCPPListElement
            patient={patient}
            bubble={bubble}
            title="History"
            field="history"
            icon="clock-rotate-left"
            items={hr.history}
            setActiveCPPTab={setActiveCPPTab}
            type={HISTORY_TYPES.ethnicity_history}
          />
          <DefaultDivider />
          <HistoryCPPListElement
            patient={patient}
            bubble={bubble}
            title="History"
            field="history"
            icon="clock-rotate-left"
            items={hr.history}
            setActiveCPPTab={setActiveCPPTab}
            type={HISTORY_TYPES.family_history}
          />
          <GTPALList
            gtpal={hr.gtpal}
            information={hr.information}
            setActiveCPPTab={setActiveCPPTab}
            patient={patient}
            bubble={bubble}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Preventive Scr..."
            field="preventative_screenings"
            icon="microscope"
            items={hr.preventative_screenings}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPListElement
            patient={patient}
            bubble={bubble}
            title="Lifestyle"
            field="lifestyle_factors"
            icon="dumbbell"
            items={hr.lifestyle_factors}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPInformationPanel patient={patient} information={hr.information} />
          <DefaultDivider />
          <CPPRemindersPanel patient={patient} />
          <DefaultDivider />
          <TasksContent
            patient={patient}
            bubble={bubble}
            tasks={patientTasks}
            setActiveCPPTab={setActiveCPPTab}
          />
          <DefaultDivider />
          <CPPListMetrics patient={patient} />
          <DefaultDivider />
          <CPPListDocuments
            patient={patient}
            bubble={bubble}
            title="Documents"
            icon="files-medical"
            setActiveCPPTab={setActiveCPPTab}
          />
        </List>
      </ErrorBoundary>
    </div>
  );
};

const TasksContent = ({ patient, bubble, tasks = DEFAULT_ARRAY, setActiveCPPTab }) => {
  const { activeTasks, archivedTasks } = useMemo(() => {
    let sorted = PerformanceUtils.sortBy(tasks, 'date');
    return {
      activeTasks: sorted.filter((task) => !task.archived),
      archivedTasks: sorted.filter((task) => task.archived),
    };
  }, [tasks]);

  return (
    tasks && (
      <div className="tasks-wrapper">
        <CPPListElement
          patient={patient}
          bubble={bubble}
          title="Tasks"
          field="tasks"
          icon="circle-check"
          items={activeTasks}
          setActiveCPPTab={setActiveCPPTab}
        />
        {archivedTasks.length > 0 && (
          <UncontrolledCollapse title={`Archived Tasks (${archivedTasks.length})`}>
            <CPPListElement
              patient={patient}
              bubble={bubble}
              title=""
              field="tasks"
              icon=""
              items={archivedTasks}
              setActiveCPPTab={setActiveCPPTab}
            />
          </UncontrolledCollapse>
        )}
      </div>
    )
  );
};
export default CPPHealthRecordsPanel;
